import { useCallback, useMemo } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { toast } from './use-toast';
import { PRODUCT_COLUMNS, STOCK_ENTRY_COLUMNS } from '@/lib/productCosts'; // PROD-CUSTO-RLS 15a

export const useMultiTenant = () => {
  const { company, user, loading, activeCompanyId } = useAuth();
  const queryClient = useQueryClient();

  // Quando o usuário fez switch para outra empresa, usar o ID ativo.
  // Fallback para company?.id (empresa home) quando:
  //   - activeCompanyId é null (visão consolidada do grupo)
  //   - activeCompanyId ainda não foi populado (logo após login)
  const effectiveCompanyId: string | undefined =
    (activeCompanyId ?? company?.id) || undefined;

  // Buscar objeto completo da empresa ativa quando diferente da home.
  // Usado por PDFs, cabeçalhos e outros componentes que precisam do CNPJ/logo.
  const { data: activeCompanyData } = useQuery({
    queryKey: ['company-data', activeCompanyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('id', activeCompanyId!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!activeCompanyId && activeCompanyId !== company?.id,
    staleTime: 5 * 60 * 1000,
  });

  // activeCompany: objeto da empresa realmente ativa (empresa B após switch,
  // ou empresa home quando não há switch).
  const activeCompany = activeCompanyData ?? company;

  const ensureCompanyContext = useCallback((): string | undefined => {
    if (loading) {
      return undefined;
    }
    
    if (!effectiveCompanyId || !user) {
      console.error('❌ CONTEXT: Missing data - EffectiveCompanyId:', effectiveCompanyId, 'User:', !!user);
      toast({
        title: "Erro de contexto",
        description: "Usuário ou empresa não encontrados",
        variant: "destructive",
      });
      return undefined;
    }
    
    return effectiveCompanyId;
  }, [loading, effectiveCompanyId, user]);

  // ✅ OTIMIZADO: Estado estável para isContextReady
  const isContextReady = useMemo(() => {
    return !loading && !!effectiveCompanyId && !!user?.id;
  }, [loading, effectiveCompanyId, user?.id]);

  // Helper functions for specific tables with proper typing
  const getCustomers = async () => {
    console.warn('⚠️ DEPRECATED: getCustomers() - Use useSecureCustomerData hook instead for better security and PII protection');
    const companyId = ensureCompanyContext();
    if (!companyId) return [];
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .eq('company_id', companyId);
      
    if (error) throw error;
    return data || [];
  };

  const insertCustomer = (data: any) => {
    const companyId = ensureCompanyContext();
    if (!companyId) return;
    return supabase
      .from('customers')
      .insert({ ...data, company_id: companyId, created_by: user?.id });
  };

  // Employees - DEPRECATED: Use profiles instead
  // These functions are deprecated and will be removed on 2025-11-05
  // Use the unified user management in UserManagementView.tsx
  const getEmployees = async () => {
    console.warn('⚠️ DEPRECATED: getEmployees() - Use useSecureProfiles hook instead');
    throw new Error('Função descontinuada. Use a interface de Usuários.');
  };

  const insertEmployee = (data: any) => {
    console.warn('⚠️ DEPRECATED: insertEmployee() - Use create-user-account edge function instead');
    throw new Error('Função descontinuada. Use a interface de Usuários.');
  };
  
  const getAvailableUsers = async () => {
    console.warn('⚠️ DEPRECATED: getAvailableUsers() - No longer needed');
    throw new Error('Função descontinuada.');
  };

  const linkEmployeeToUser = async (employeeId: string, userId: string) => {
    console.warn('⚠️ DEPRECATED: linkEmployeeToUser() - No longer needed');
    throw new Error('Função descontinuada.');
  };

  const getCylinders = () => {
    const companyId = ensureCompanyContext();
    if (!companyId) return;
    return supabase
      .from('cylinders')
      .select(`*, product:products(${PRODUCT_COLUMNS})`) // PROD-CUSTO-RLS 15a
      .eq('company_id', companyId);
  };

  const insertCylinder = (data: any) => {
    const companyId = ensureCompanyContext();
    if (!companyId) return;
    return supabase
      .from('cylinders')
      .insert({ ...data, company_id: companyId, created_by: user?.id });
  };

  const getEquipment = () => {
    const companyId = ensureCompanyContext();
    if (!companyId) return;
    return supabase
      .from('equipment')
      .select(`*, product:products(${PRODUCT_COLUMNS})`) // PROD-CUSTO-RLS 15a
      .eq('company_id', companyId);
  };

  const insertEquipment = (data: any) => {
    const companyId = ensureCompanyContext();
    if (!companyId) return;
    return supabase
      .from('equipment')
      .insert({ ...data, company_id: companyId, created_by: user?.id });
  };

  const getProductStats = async () => {
    const companyId = ensureCompanyContext();
    if (!companyId) return null;
    
    try {
      // Use Promise.all to execute all counts in parallel with HEAD requests
      // PROD-CUSTO-RLS 15a: contagem por id (a linha inteira tem custo, que o papel do app deixa de ler no Ato 2)
      // m-PROD.3: os cartões não contam produto excluído (deleted_at), como a lista de produtos
      const [
        totalResult,
        activeResult,
        activeCylindersResult,
        activeEquipmentResult,
        activeGasResult,
        activeProductsResult,
        inactiveCylindersResult,
        inactiveEquipmentResult,
        inactiveGasResult,
        inactiveProductsResult
      ] = await Promise.all([
        // Total count
        supabase
          .from('products')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .is('deleted_at', null),
        
        // Active count
        supabase
          .from('products')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .is('deleted_at', null)
          .eq('active', true),
        
        // Active cylinders
        supabase
          .from('products')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .is('deleted_at', null)
          .eq('active', true)
          .eq('product_type', 'cylinder'),
        
        // Active equipment
        supabase
          .from('products')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .is('deleted_at', null)
          .eq('active', true)
          .eq('product_type', 'equipment'),
        
        // Active gas
        supabase
          .from('products')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .is('deleted_at', null)
          .eq('active', true)
          .eq('product_type', 'gas'),
        
        // Active products
        supabase
          .from('products')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .is('deleted_at', null)
          .eq('active', true)
          .eq('product_type', 'product'),
        
        // Inactive cylinders
        supabase
          .from('products')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .is('deleted_at', null)
          .eq('active', false)
          .eq('product_type', 'cylinder'),
        
        // Inactive equipment
        supabase
          .from('products')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .is('deleted_at', null)
          .eq('active', false)
          .eq('product_type', 'equipment'),
        
        // Inactive gas
        supabase
          .from('products')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .is('deleted_at', null)
          .eq('active', false)
          .eq('product_type', 'gas'),
        
        // Inactive products
        supabase
          .from('products')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .is('deleted_at', null)
          .eq('active', false)
          .eq('product_type', 'product')
      ]);
      
      const totalCount = totalResult.count || 0;
      const activeCount = activeResult.count || 0;
      
      return {
        activeCylinders: activeCylindersResult.count || 0,
        inactiveCylinders: inactiveCylindersResult.count || 0,
        activeEquipment: activeEquipmentResult.count || 0,
        inactiveEquipment: inactiveEquipmentResult.count || 0,
        activeGas: activeGasResult.count || 0,
        inactiveGas: inactiveGasResult.count || 0,
        activeProducts: activeProductsResult.count || 0,
        inactiveProducts: inactiveProductsResult.count || 0,
        totalActive: activeCount,
        totalInactive: totalCount - activeCount,
        total: totalCount
      };
    } catch (error) {
      console.error('Error fetching product stats:', error);
      throw error;
    }
  };

  const getJobPositions = () => {
    const companyId = ensureCompanyContext();
    if (!companyId) return;
    return supabase
      .from('job_positions')
      .select('*')
      .eq('company_id', companyId)
      .eq('active', true);
  };

  const insertJobPosition = (data: any) => {
    const companyId = ensureCompanyContext();
    if (!companyId) return;
    return supabase
      .from('job_positions')
      .insert({ ...data, company_id: companyId, created_by: user?.id });
  };

  const getDeposits = () => {
    const companyId = ensureCompanyContext();
    if (!companyId) return;
    return supabase
      .from('deposits')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });
  };

  const insertDeposit = (data: any) => {
    const companyId = ensureCompanyContext();
    if (!companyId) return;
    return supabase
      .from('deposits')
      .insert({ ...data, company_id: companyId, created_by: user?.id });
  };

  const getDepositCylinders = (depositId: string) => {
    const companyId = ensureCompanyContext();
    if (!companyId) return;
    return supabase
      .from('cylinders')
      .select(`
        *,
        product:products(name, sku, capacity_liters)
      `)
      .eq('company_id', companyId)
      .eq('current_deposit_id', depositId)
      .order('serial_number');
  };

  const getDepositEquipment = (depositId: string) => {
    const companyId = ensureCompanyContext();
    if (!companyId) return;
    return supabase
      .from('equipment')
      .select(`
        *,
        product:products(name, sku, category, brand)
      `)
      .eq('company_id', companyId)
      .eq('current_deposit_id', depositId)
      .order('name');
  };

  const getEquipmentCategories = async () => {
    const companyId = ensureCompanyContext();
    if (!companyId) return [];

    const { data, error } = await supabase
      .from('equipment_categories')
      .select('*')
      .eq('company_id', companyId)
      .eq('active', true)
      .order('name');

    if (error) throw error;
    return data;
  };

  const insertEquipmentCategory = async (categoryData: any) => {
    const companyId = ensureCompanyContext();
    if (!companyId) return null;

    const { data, error } = await supabase
      .from('equipment_categories')
      .insert({
        ...categoryData,
        company_id: companyId,
        created_by: user?.id
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  };

  const getBrands = async () => {
    const companyId = ensureCompanyContext();
    if (!companyId) return [];

    const { data, error } = await supabase
      .from('brands')
      .select('*')
      .eq('company_id', companyId)
      .eq('active', true)
      .order('name');

    if (error) throw error;
    return data;
  };

  const insertBrand = async (brandData: any) => {
    const companyId = ensureCompanyContext();
    if (!companyId) return null;

    const { data, error } = await supabase
      .from('brands')
      .insert({
        ...brandData,
        company_id: companyId,
        created_by: user?.id
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  };

  const getMeasurementUnits = async () => {
    const companyId = ensureCompanyContext();
    if (!companyId) return [];

    const { data, error } = await supabase
      .from('measurement_units')
      .select('*')
      .eq('company_id', companyId)
      .eq('active', true)
      .order('name');

    if (error) throw error;
    return data;
  };

  const insertMeasurementUnit = async (unitData: any) => {
    const companyId = ensureCompanyContext();
    if (!companyId) return null;

    const { data, error } = await supabase
      .from('measurement_units')
      .insert({
        ...unitData,
        company_id: companyId,
        created_by: user?.id
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  };

  const updateMeasurementUnit = async (id: string, unitData: any) => {
    const companyId = ensureCompanyContext();
    if (!companyId) return null;
    const { data, error } = await supabase
      .from('measurement_units')
      .update(unitData)
      .eq('company_id', companyId)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  };

  const deleteMeasurementUnit = async (id: string) => {
    const companyId = ensureCompanyContext();
    if (!companyId) return;
    const { error } = await supabase
      .from('measurement_units')
      .delete()
      .eq('company_id', companyId)
      .eq('id', id);

    if (error) throw error;
  };

  const getStockEntries = () => {
    const companyId = ensureCompanyContext();
    if (!companyId) return;
    return supabase
      .from('stock_entries')
      .select(STOCK_ENTRY_COLUMNS) // PROD-CUSTO-RLS 15a: sem o custo da entrada
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });
  };

  const insertStockEntry = (data: any) => {
    const companyId = ensureCompanyContext();
    if (!companyId) return;
    return supabase
      .from('stock_entries')
      .insert({ ...data, company_id: companyId, created_by: user?.id });
  };

  // Loans CRUD operations
  const getLoans = useCallback(async () => {
    const companyId = ensureCompanyContext();
    if (!companyId) return [];
    
    // Buscar empréstimos com joins nativos (FKs criadas na migration)
    const { data: loansData, error: loansError } = await supabase
      .from('loans')
      .select(`
        id, loan_number, loan_type, status, loan_date,
        expected_return_date, actual_return_date,
        customer_id, vehicle_deposit_id, notes,
        created_by, created_at, updated_at, deleted_at,
        company_id, movement_id,
        signature_data, signature_date, signature_customer_name,
        signature_customer_document, signature_ip,
        email_sent_at, email_sent_to, email_read_at,
        email_read_confirmed, expiration_notified_at,
        customers(name, email, phone, document_type, document_number),
        movements(
          id,
          movement_items(
            id,
            cylinder_id,
            equipment_id,
            cylinder_type_id,
            equipment_type_id,
            quantity,
            cylinders(serial_number),
            equipment(name),
            product_cylinder:products!movement_items_cylinder_type_id_fkey(id, name, sku, brand),
            product_equipment:products!movement_items_equipment_type_id_fkey(id, name, sku, brand)
          )
        )
      `)
      .eq('company_id', companyId)
      .in('status', ['active', 'partial_returned', 'pending_collection'])
      .order('created_at', { ascending: false });

    if (loansError) {
      console.error('Error fetching loans:', loansError);
      throw loansError;
    }

    // Dados já enriquecidos pelo join nativo
    const enrichedLoans = loansData;

    // Fetch profiles separately
    const createdByIds = [...new Set(loansData?.map(l => l.created_by).filter(Boolean))];
    let profiles = [];
    
    if (createdByIds.length > 0) {
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, full_name')
        .in('user_id', createdByIds);
      
      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
      } else {
        profiles = profilesData || [];
      }
    }

    // OPTIMIZATION: Fetch all loan_returns and loan_return_items in bulk (outside the loop)
    // This eliminates N+1 queries and prevents the infinite loading issue
    let allReturns: any[] = [];
    let allReturnItems: any[] = [];
    
    try {
      const partialReturnedLoans = (loansData || []).filter(l => l.status === 'partial_returned');
      
      if (partialReturnedLoans.length > 0) {
        const loanIds = partialReturnedLoans.map(l => l.id);
        
        // Fetch all loan_returns for partial_returned loans
        const { data: returnsData, error: returnsError } = await supabase
          .from('loan_returns')
          .select('id, loan_id')
          .in('loan_id', loanIds);
        
        if (returnsError) {
          console.error('Error fetching loan_returns:', returnsError);
        } else {
          allReturns = returnsData || [];
          
          // Fetch all loan_return_items for these returns
          if (allReturns.length > 0) {
            const returnIds = allReturns.map(r => r.id);
            
            const { data: returnItemsData, error: returnItemsError } = await supabase
              .from('loan_return_items')
              .select('*')
              .in('loan_return_id', returnIds);
            
            if (returnItemsError) {
              console.error('Error fetching loan_return_items:', returnItemsError);
            } else {
              allReturnItems = returnItemsData || [];
            }
          }
        }
      }
    } catch (error) {
      console.error('Error in bulk fetch of returns:', error);
      // Continue without return data - loans will show original quantities
    }

    // Process loans with in-memory mapping (usando enrichedLoans com dados de produtos)
    const processedLoans = [];
    
    for (const loan of enrichedLoans || []) {
      const profile = profiles.find(p => p.user_id === loan.created_by);
      let processedLoan = {
        ...loan,
        created_by_profile: profile ? { full_name: profile.full_name } : null
      };

      // If loan has partial returns, calculate remaining items using in-memory data
      if (loan.status === 'partial_returned') {
        try {
          // Find all returns for this loan
          const loanReturnIds = allReturns
            .filter(r => r.loan_id === loan.id)
            .map(r => r.id);
          
          // Find all return items for these returns
          const returnItems = allReturnItems.filter(ri => 
            loanReturnIds.includes(ri.loan_return_id)
          );

          // Calculate returned quantities per movement item
          const returnedQuantities: { [key: string]: number } = {};
          returnItems.forEach(returnItem => {
            const itemId = returnItem.movement_item_id;
            returnedQuantities[itemId] = (returnedQuantities[itemId] || 0) + returnItem.returned_quantity;
          });

          // §I.97 Ato 6 — manter a quantidade ORIGINAL e expor returned_quantity
          // por item; pendente = quantity - returned_quantity fica a cargo da UI
          // ("Itens do Empréstimo" mostra originais, "Itens Emprestados" só pendentes)
          if (processedLoan.movements?.movement_items) {
            processedLoan.movements.movement_items = processedLoan.movements.movement_items
              .map(item => ({
                ...item,
                returned_quantity: returnedQuantities[item.id] || 0
              }));
          }
        } catch (error) {
          console.error(`Error processing returns for loan ${loan.id}:`, error);
          // Continue with original loan data if processing fails
        }
      }

      processedLoans.push(processedLoan);
    }
    
    return processedLoans;
  }, [ensureCompanyContext, user?.id]);

  const insertLoan = (data: any) => {
    const companyId = ensureCompanyContext();
    if (!companyId) return;
    return supabase
      .from('loans')
      .insert({ ...data, company_id: companyId, created_by: user?.id })
      .select()
      .single();
  };

  // Rentals CRUD operations
  const getRentals = async () => {
    const companyId = ensureCompanyContext();
    if (!companyId) return [];
    
    const { data: rentalsData, error: rentalsError } = await supabase
      .from('rentals')
      .select(`
        *,
        customers(name, email, phone, document_type, document_number)
      `)
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });

    if (rentalsError) {
      console.error('Error fetching rentals:', rentalsError);
      throw rentalsError;
    }

    // Fetch profiles separately
    const createdByIds = [...new Set(rentalsData?.map(r => r.created_by).filter(Boolean))];
    let profiles = [];
    
    if (createdByIds.length > 0) {
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, full_name')
        .in('user_id', createdByIds);
      
      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
      } else {
        profiles = profilesData || [];
      }
    }

    // Join profiles manually
    const rentalsWithProfiles = rentalsData?.map(rental => {
      const profile = profiles.find(p => p.user_id === rental.created_by);
      return {
        ...rental,
        created_by_profile: profile ? { full_name: profile.full_name } : null
      };
    }) || [];
    
    return rentalsWithProfiles;
  };

  // Movements CRUD operations
  const getMovements = () => {
    const companyId = ensureCompanyContext();
    if (!companyId) return;
    return supabase
      .from('movements')
      .select(`
        *,
        customer:customers(*),
        employee:employees(*),
        from_deposit:deposits!movements_from_deposit_id_fkey(*),
        to_deposit:deposits!movements_to_deposit_id_fkey(*),
        movement_items(*)
      `)
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });
  };

  const insertMovement = (data: any) => {
    const companyId = ensureCompanyContext();
    if (!companyId) return;
    return supabase
      .from('movements')
      .insert({ ...data, company_id: companyId, created_by: user?.id })
      .select()
      .single();
  };

  const insertMovementItem = (data: any) => {
    const companyId = ensureCompanyContext();
    if (!companyId) return;
    return supabase
      .from('movement_items')
      .insert({ ...data, company_id: companyId })
      .select()
      .single();
  };

  // Product Types operations (replacing legacy cylinder_types and equipment_types)
  const getCylinderTypes = async () => {
    const companyId = ensureCompanyContext();
    if (!companyId) return [];
    const { data, error } = await supabase
      .from('products')
      .select(`${PRODUCT_COLUMNS}, measurement_unit:measurement_units(name)`) // PROD-CUSTO-RLS 15a
      .eq('company_id', companyId)
      .eq('product_type', 'cylinder')
      .order('created_at', { ascending: false });
      
    if (error) throw error;
    return data || [];
  };

  // Equipment Types operations  
  const getEquipmentTypes = async () => {
    const companyId = ensureCompanyContext();
    if (!companyId) return [];
    const { data, error } = await supabase
      .from('products')
      .select(PRODUCT_COLUMNS) // PROD-CUSTO-RLS 15a
      .eq('company_id', companyId)
      .eq('product_type', 'equipment')
      .order('created_at', { ascending: false });
      
    if (error) throw error;
    return data || [];
  };

  // Generate loan number using RPC for unified ROM- sequence
  const generateLoanNumber = async () => {
    const companyId = ensureCompanyContext();
    if (!companyId) return 'ROM-0001';
    
    // Usar RPC para gerar número único de romaneio
    const { data, error } = await supabase
      .rpc('generate_next_romaneio_number', { p_company_id: companyId });
    
    if (error) {
      console.error('Erro ao gerar número de romaneio via RPC:', error);
      // Fallback para geração local caso RPC falhe
      const { data: allLoans, error: fallbackError } = await supabase
        .from('loans')
        .select('loan_number')
        .eq('company_id', companyId)
        .order('loan_number', { ascending: false })
        .limit(100);

      // LOTE C §I.98 — o fallback NÃO pode numerar como se a tabela
      // estivesse vazia (risco de colisão de ROM-)
      if (fallbackError) throw fallbackError;

      let maxNumber = 0;
      for (const loan of allLoans || []) {
        if (loan.loan_number) {
          const num = parseInt(loan.loan_number.replace(/\D/g, ''), 10);
          if (!isNaN(num) && num > 0 && num <= 9999 && num > maxNumber) {
            maxNumber = num;
          }
        }
      }
      return `ROM-${String(maxNumber + 1).padStart(4, '0')}`;
    }
    
    return data || 'ROM-0001';
  };

  // Generate rental number
  const generateRentalNumber = async () => {
    const companyId = ensureCompanyContext();
    if (!companyId) return 'LOC-0001';
    
    // Buscar TODOS os rental_numbers (incluindo deletados) para evitar reutilização
    const { data: allRentals } = await supabase
      .from('rentals')
      .select('rental_number')
      .eq('company_id', companyId)
      .order('rental_number', { ascending: false })
      .limit(100);

    if (!allRentals || allRentals.length === 0) {
      return 'LOC-0001';
    }

    // Extrair o maior número já usado (ignorando números anormais)
    let maxNumber = 0;
    for (const rental of allRentals) {
      if (rental.rental_number) {
        const num = parseInt(rental.rental_number.replace(/\D/g, ''), 10);
        
        // PROTEÇÃO: Ignorar números absurdos (timestamps, etc)
        // Números válidos devem estar entre 1 e 9999 (formato LOC-0001 até LOC-9999)
        if (!isNaN(num) && num > 0 && num <= 9999 && num > maxNumber) {
          maxNumber = num;
        }
      }
    }

    // Incrementar +1 para o próximo
    const nextNumber = maxNumber + 1;
    return `LOC-${String(nextNumber).padStart(4, '0')}`;
  };

  // Get active loans for collection
  const getActiveLoans = async () => {
    const companyId = ensureCompanyContext();
    if (!companyId) return [];
    
    const { data: loans, error } = await supabase
      .from('loans')
      .select(`
        *,
        customers!inner(name, email, phone, document_type, document_number),
        movements!inner(
          id,
          movement_items(
            id,
            cylinder_id,
            equipment_id,
            cylinder_type_id,
            equipment_type_id,
            quantity,
            cylinders(serial_number),
            equipment(name),
            product_cylinder:products!cylinder_type_id(name, sku, sale_price),
            product_equipment:products!equipment_type_id(name, sku, brand, sale_price)
          )
        )
      `)
      .eq('company_id', companyId)
      .in('status', ['active', 'partial_returned'])
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Process loans to handle partial returns correctly
    const processedLoans = [];
    
    for (const loan of loans || []) {
      let processedLoan = { ...loan };

      // If loan has partial returns, calculate remaining items
      if (loan.status === 'partial_returned') {
        // §I.97 Ato 3 — loan_return_items não tem FK para loan_returns, então o
        // embed PostgREST (loan_returns!inner) falhava silenciosamente e as
        // quantidades vinham cheias. Busca em duas etapas via .in().
        const { data: loanReturns, error: loanReturnsError } = await supabase
          .from('loan_returns')
          .select('id')
          .eq('loan_id', loan.id);
        if (loanReturnsError) throw loanReturnsError;
        const returnIds = (loanReturns || []).map(lr => lr.id);

        let returnItems: { movement_item_id: string; returned_quantity: number }[] = [];
        if (returnIds.length > 0) {
          // LOTE C §I.98 — falha silenciosa aqui deixava as quantidades
          // "cheias" no RecolhimentoModal (padrão do bug histórico §I.97)
          const { data, error: returnItemsError } = await supabase
            .from('loan_return_items')
            .select('movement_item_id, returned_quantity')
            .in('loan_return_id', returnIds);
          if (returnItemsError) throw returnItemsError;
          returnItems = data || [];
        }

        // Calculate returned quantities per movement item
        const returnedQuantities: { [key: string]: number } = {};
        returnItems.forEach(returnItem => {
          const itemId = returnItem.movement_item_id;
          returnedQuantities[itemId] = (returnedQuantities[itemId] || 0) + returnItem.returned_quantity;
        });

        // Reduce quantities to the pending amount, keeping fully returned items
        // (the modal shows them disabled with "Devolvido X de Y")
        if (processedLoan.movements?.movement_items) {
          processedLoan.movements.movement_items = processedLoan.movements.movement_items
            .map(item => {
              const returnedQty = returnedQuantities[item.id] || 0;
              const remainingQty = Math.max(0, item.quantity - returnedQty);

              return {
                ...item,
                quantity: remainingQty,
                returned_quantity: returnedQty
              };
            });
        }
      }

      processedLoans.push(processedLoan);
    }

    return processedLoans.map(loan => ({
      id: loan.id,
      loan_number: loan.loan_number,
      customer_name: loan.customers.name,
      customer_id: loan.customer_id,
      loan_date: loan.loan_date,
      expected_return_date: loan.expected_return_date,
      status: loan.status,
      loan_type: loan.loan_type,
      total_items: loan.movements.movement_items?.length || 0,
      items: loan.movements.movement_items?.map(item => {
        // Create descriptive names for cylinders
        let cylinder_name = '';
        if (item.cylinder_id && item.cylinders) {
          cylinder_name = `Cilindro (${item.cylinders.serial_number})`;
        } else if (item.cylinder_type_id && item.product_cylinder) {
          const typeName = item.product_cylinder.name || 'Tipo não identificado';
          const sku = item.product_cylinder.sku ? ` - ${item.product_cylinder.sku}` : '';
          cylinder_name = `${typeName}${sku}`;
        }

        // Create descriptive names for equipment
        let equipment_name = '';
        if (item.equipment_id && item.equipment) {
          equipment_name = item.equipment.name;
        } else if (item.equipment_type_id && item.product_equipment) {
          const typeName = item.product_equipment.name || 'Equipamento';
          const brand = item.product_equipment.brand ? ` - ${item.product_equipment.brand}` : '';
          const sku = item.product_equipment.sku ? ` (${item.product_equipment.sku})` : '';
          equipment_name = `${typeName}${brand}${sku}`;
        }

        return {
          id: item.id,
          cylinder_id: item.cylinder_id,
          equipment_id: item.equipment_id,
          cylinder_name,
          equipment_name,
          quantity: item.quantity,
          returned_quantity: item.returned_quantity ?? 0,
          selected: false,
          selectedQuantity: 0,
          product_id: item.cylinder_type_id ?? item.equipment_type_id ?? undefined,
          product_name: cylinder_name || equipment_name || undefined,
          sale_price: item.product_cylinder?.sale_price ?? item.product_equipment?.sale_price ?? undefined,
        };
      }) || []
    }));
  };

  // Get loan returns
  const getLoanReturns = async () => {
    const companyId = ensureCompanyContext();
    if (!companyId) return [];
    
    try {
      // Simplified query to avoid complex joins that might fail
      const { data: returns, error } = await supabase
        .from('loan_returns')
        .select(`
          id,
          loan_id,
          return_date,
          return_type,
          notes,
          created_at,
          loans!inner (
            loan_number,
            customers!inner (name, document_number)
          )
        `)
        .eq('company_id', companyId)
        .order('return_date', { ascending: false });

      if (error) {
        console.error('Error fetching loan returns:', error);
        throw error;
      }

      // Get return items separately for each return
      const enrichedReturns = await Promise.all(
        (returns || []).map(async (loanReturn) => {
          const { data: returnItems, error: returnItemsError } = await supabase
            .from('loan_return_items')
            .select(`
              id,
              returned_quantity,
              movement_items!inner (
                id,
                quantity,
                product_cylinder:products!cylinder_type_id(name, capacity_liters),
                product_equipment:products!equipment_type_id(name, brand, sku),
                cylinders (serial_number),
                equipment (serial_number, name)
              )
            `)
            .eq('loan_return_id', loanReturn.id);
          if (returnItemsError) throw returnItemsError;

          return {
            ...loanReturn,
            loan_return_items: returnItems || []
          };
        })
      );

      return enrichedReturns;
    } catch (error) {
      console.error('Error in getLoanReturns:', error);
      return [];
    }
  };

  // §I.97 Ato 5 — eventos de devolução de um romaneio (documento vivo).
  // Duas etapas via .in() — sem embed loan_returns↔loan_return_items,
  // pois types.ts ainda não foi regenerado após a FK do Ato 4.
  const getLoanReturnEvents = useCallback(async (loanId: string) => {
    const companyId = ensureCompanyContext();
    if (!companyId) return [];

    const { data: returnsData, error: returnsError } = await supabase
      .from('loan_returns')
      .select('*')
      .eq('loan_id', loanId)
      .eq('company_id', companyId)
      .order('return_date', { ascending: true });

    if (returnsError) throw returnsError;

    // DÉBITO: regenerar types (fila §I.97+) — new_expected_return_date (Ato 2)
    // ainda não existe no Row de loan_returns em types.ts.
    const returns = (returnsData || []) as Array<{
      id: string;
      return_date: string;
      return_type: string;
      notes: string | null;
      created_by: string | null;
      new_expected_return_date?: string | null;
    }>;
    if (returns.length === 0) return [];

    const { data: itemRows, error: itemsError } = await supabase
      .from('loan_return_items')
      .select(`
        loan_return_id,
        returned_quantity,
        movement_items (
          id,
          cylinder_id,
          equipment_id,
          cylinder_type_id,
          equipment_type_id,
          cylinders(serial_number),
          equipment(name),
          product_cylinder:products!movement_items_cylinder_type_id_fkey(name, sku),
          product_equipment:products!movement_items_equipment_type_id_fkey(name, sku, brand)
        )
      `)
      .in('loan_return_id', returns.map(r => r.id));

    if (itemsError) throw itemsError;

    const describeMovementItem = (mi: any): string => {
      if (mi?.cylinder_id && mi?.cylinders) {
        return `Cilindro (${mi.cylinders.serial_number})`;
      }
      if (mi?.cylinder_type_id && mi?.product_cylinder) {
        const sku = mi.product_cylinder.sku ? ` - ${mi.product_cylinder.sku}` : '';
        return `${mi.product_cylinder.name || 'Cilindro'}${sku}`;
      }
      if (mi?.equipment_id && mi?.equipment) {
        return mi.equipment.name || 'Equipamento';
      }
      if (mi?.equipment_type_id && mi?.product_equipment) {
        const brand = mi.product_equipment.brand ? ` - ${mi.product_equipment.brand}` : '';
        return `${mi.product_equipment.name || 'Equipamento'}${brand}`;
      }
      return 'Item';
    };

    const itemsByReturn: Record<string, Array<{ name: string; qty: number }>> = {};
    (itemRows || []).forEach((row: any) => {
      const list = itemsByReturn[row.loan_return_id] || [];
      list.push({
        name: describeMovementItem(row.movement_items),
        qty: row.returned_quantity,
      });
      itemsByReturn[row.loan_return_id] = list;
    });

    // §I.98 Ato 3 — quem registrou cada evento (mesmo padrão de getLoans)
    const createdByIds = [...new Set(returns.map(r => r.created_by).filter(Boolean))] as string[];
    let profiles: Array<{ user_id: string; full_name: string | null }> = [];
    if (createdByIds.length > 0) {
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, full_name')
        .in('user_id', createdByIds);

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
      } else {
        profiles = profilesData || [];
      }
    }

    return returns.map(r => {
      const profile = r.created_by ? profiles.find(p => p.user_id === r.created_by) : undefined;
      return {
        id: r.id,
        returnDate: r.return_date,
        returnType: r.return_type,
        notes: r.notes,
        newExpectedReturnDate: r.new_expected_return_date ?? null,
        collectedBy: profile ? { userId: profile.user_id, fullName: profile.full_name ?? '' } : null,
        items: itemsByReturn[r.id] || [],
      };
    });
  }, [ensureCompanyContext]);

  // Process loan return (RPC atômica: estoque + parcial por quantidade)
  const processLoanReturn = useCallback(
    async (returnData: {
      loanId: string;
      items: { id: string; quantity: number }[];
      notes?: string;
      returnDate: string;
      /** §I.97 Ato 2/3 — repactuação da expectativa dos remanescentes ('yyyy-MM-dd'; só comodato + devolução parcial) */
      newExpectedReturnDate?: string;
    }) => {
      const companyId = ensureCompanyContext();
      if (!companyId) return;
      if (!user?.id) {
        throw new Error('Usuário não autenticado');
      }

      const p_items = returnData.items.map((item) => ({
        movement_item_id: item.id,
        returned_quantity: item.quantity,
      }));

      const rpcArgs = {
        p_user_id: user.id,
        p_loan_id: returnData.loanId,
        p_items,
        p_notes: returnData.notes ?? null,
        p_return_date: returnData.returnDate,
        p_new_expected_return_date: returnData.newExpectedReturnDate ?? null,
      };

      // DÉBITO: regenerar types (fila §I.97+) — types.ts ainda lista 5 args para
      // process_loan_return_atomic; p_new_expected_return_date (Ato 2) exige este cast.
      const { data, error } = await supabase.rpc(
        'process_loan_return_atomic',
        rpcArgs as unknown as Database['public']['Functions']['process_loan_return_atomic']['Args']
      );

      if (error) throw error;

      const row = data as {
        is_partial_return?: boolean;
        total_loaned?: number;
        total_returned_after?: number;
      } | null;

      await queryClient.invalidateQueries({ queryKey: ['loans'] });
      await queryClient.invalidateQueries({ queryKey: ['driver-loans'] });
      await queryClient.invalidateQueries({ queryKey: ['products'] });

      return {
        success: true,
        isPartialReturn: !!row?.is_partial_return,
        totalItems: row?.total_loaned ?? 0,
        returnedItems: row?.total_returned_after ?? 0,
      };
    },
    [ensureCompanyContext, user?.id, queryClient]
  );

  // Generic helper for updates (safer with explicit typing)
  const updateRecord = (tableName: 'customers' | 'employees' | 'cylinders' | 'equipment' | 'deposits' | 'products' | 'job_positions' | 'equipment_categories' | 'measurement_units' | 'stock_entries' | 'brands', id: string, data: any) => {
    const companyId = ensureCompanyContext();
    if (!companyId) return;
    return (supabase as any)
      .from(tableName)
      .update(data)
      .eq('company_id', companyId)
      .eq('id', id);
  };

  // Generic helper for deletes
  const deleteRecord = (tableName: 'customers' | 'employees' | 'cylinders' | 'equipment' | 'deposits' | 'products' | 'job_positions' | 'equipment_categories' | 'measurement_units' | 'stock_entries' | 'brands', id: string) => {
    const companyId = ensureCompanyContext();
    if (!companyId) return;
    return (supabase as any)
      .from(tableName)
      .delete()
      .eq('company_id', companyId)
      .eq('id', id);
  };

  // Process transfer between deposits
  const processTransfer = async (transferData: {
    fromDepositId: string;
    toDepositId: string;
    productType: 'cylinder' | 'equipment';
    productId: string;
    quantity: number;
    notes?: string;
  }) => {
    const companyId = ensureCompanyContext();
    if (!companyId) return;
    
    try {
      // Get individual products available for transfer
      let availableProducts: any[] = [];
      
      if (transferData.productType === 'cylinder') {
        const { data: cylinders } = await (supabase as any)
          .from('cylinders')
          .select('id, serial_number, cylinder_type_id, current_deposit_id, status, company_id')
          .eq('cylinder_type_id', transferData.productId)
          .eq('current_deposit_id', transferData.fromDepositId)
          .eq('status', 'available')
          .eq('company_id', companyId)
          .limit(transferData.quantity);
        
        availableProducts = cylinders || [];
      } else {
        const { data: equipment } = await (supabase as any)
          .from('equipment')
          .select('id, serial_number, equipment_type_id, current_deposit_id, status, company_id')
          .eq('equipment_type_id', transferData.productId)
          .eq('current_deposit_id', transferData.fromDepositId)
          .eq('status', 'available')
          .eq('company_id', companyId)
          .limit(transferData.quantity);
        
        availableProducts = equipment || [];
      }

      if (availableProducts.length < transferData.quantity) {
        throw new Error(`Apenas ${availableProducts.length} produtos disponíveis para transferência`);
      }

      // Generate movement number
      const { data: existingMovements } = await supabase
        .from('movements')
        .select('movement_number')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
        .limit(1);

      const lastMovementNumber = existingMovements?.[0]?.movement_number || 'MOV-0000';
      const nextMovementNumber = parseInt(lastMovementNumber.split('-')[1]) + 1;
      const movementNumber = `MOV-${nextMovementNumber.toString().padStart(4, '0')}`;

      // Create movement record
      const { data: movement, error: movementError } = await supabase
        .from('movements')
        .insert({
          movement_number: movementNumber,
          movement_type: 'transfer',
          from_deposit_id: transferData.fromDepositId,
          to_deposit_id: transferData.toDepositId,
          notes: transferData.notes,
          company_id: companyId,
          created_by: user?.id,
        })
        .select()
        .single();

      if (movementError) throw movementError;

      // Update individual products location and create movement items
      for (const product of availableProducts.slice(0, transferData.quantity)) {
        // Update product location
        if (transferData.productType === 'cylinder') {
          await supabase
            .from('cylinders')
            .update({ current_deposit_id: transferData.toDepositId })
            .eq('id', product.id);
          
          // Create movement item
          await supabase
            .from('movement_items')
            .insert({
              movement_id: movement.id,
              cylinder_id: product.id,
              cylinder_type_id: transferData.productId,
              quantity: 1,
              notes: transferData.notes,
              company_id: companyId
            });
        } else {
          await supabase
            .from('equipment')
            .update({ current_deposit_id: transferData.toDepositId })
            .eq('id', product.id);
          
          // Create movement item
          await supabase
            .from('movement_items')
            .insert({
              movement_id: movement.id,
              equipment_id: product.id,
              equipment_type_id: transferData.productId,
              quantity: 1,
              notes: transferData.notes,
              company_id: companyId
            });
        }
      }

      return { success: true, movement };
    } catch (error) {
      console.error('Erro ao processar transferência:', error);
      throw error;
    }
  };

  // Process multi-item transfer between deposits with gas status support
  const processMultiItemTransfer = async (transferData: {
    fromDepositId: string;
    toDepositId: string;
    transferReason: string;
    items: Array<{
      productType: 'cylinder' | 'equipment';
      productId: string;
      gasStatus?: 'full' | 'empty';
      quantity: number;
      notes?: string;
    }>;
    notes?: string;
  }) => {
    const companyId = ensureCompanyContext();
    if (!companyId) return;
    
    try {
      // Generate movement number
      const { data: existingMovements } = await supabase
        .from('movements')
        .select('movement_number')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
        .limit(1);

      const lastMovementNumber = existingMovements?.[0]?.movement_number || 'MOV-0000';
      const nextMovementNumber = parseInt(lastMovementNumber.split('-')[1]) + 1;
      const movementNumber = `MOV-${nextMovementNumber.toString().padStart(4, '0')}`;

      // Create movement record
      const { data: movement, error: movementError } = await supabase
        .from('movements')
        .insert({
          movement_number: movementNumber,
          movement_type: transferData.transferReason as any,
          from_deposit_id: transferData.fromDepositId,
          to_deposit_id: transferData.toDepositId,
          transfer_reason: transferData.transferReason,
          notes: transferData.notes,
          company_id: companyId,
          created_by: user?.id,
        })
        .select()
        .single();

      if (movementError) throw movementError;

      // Process each item in the transfer
      for (const item of transferData.items) {
        // Get individual products available for transfer with gas status filter
        let availableProducts: any[] = [];
        
        if (item.productType === 'cylinder') {
          const query = (supabase as any)
            .from('cylinders')
            .select('id, serial_number, cylinder_type_id, current_deposit_id, status, company_id, gas_status')
            .eq('cylinder_type_id', item.productId)
            .eq('current_deposit_id', transferData.fromDepositId)
            .eq('status', 'available')
            .eq('company_id', companyId);

          // Add gas status filter if specified
          if (item.gasStatus) {
            query.eq('gas_status', item.gasStatus);
          }

          const { data: cylinders } = await query.limit(item.quantity);
          availableProducts = cylinders || [];
        } else {
          const { data: equipment } = await (supabase as any)
            .from('equipment')
            .select('id, serial_number, equipment_type_id, current_deposit_id, status, company_id')
            .eq('equipment_type_id', item.productId)
            .eq('current_deposit_id', transferData.fromDepositId)
            .eq('status', 'available')
            .eq('company_id', companyId)
            .limit(item.quantity);
          
          availableProducts = equipment || [];
        }

        if (availableProducts.length < item.quantity) {
          throw new Error(`Apenas ${availableProducts.length} produtos disponíveis para transferência do item ${item.productType}`);
        }

        // Update individual products location and create movement items
        for (const product of availableProducts.slice(0, item.quantity)) {
          if (item.productType === 'cylinder') {
            await supabase
              .from('cylinders')
              .update({ current_deposit_id: transferData.toDepositId })
              .eq('id', product.id);
            
            // Create movement item
            await supabase
              .from('movement_items')
              .insert({
                movement_id: movement.id,
                cylinder_id: product.id,
                cylinder_type_id: item.productId,
                quantity: 1,
                notes: item.notes,
                company_id: companyId
              });

            // Create transfer item for detailed tracking
            await supabase
              .from('transfer_items')
              .insert({
                movement_id: movement.id,
                product_type: 'cylinder',
                product_id: item.productId,
                gas_status: item.gasStatus,
                quantity: 1,
                notes: item.notes,
                company_id: companyId,
                created_by: user?.id
              });
          } else {
            await supabase
              .from('equipment')
              .update({ current_deposit_id: transferData.toDepositId })
              .eq('id', product.id);
            
            // Create movement item
            await supabase
              .from('movement_items')
              .insert({
                movement_id: movement.id,
                equipment_id: product.id,
                equipment_type_id: item.productId,
                quantity: 1,
                notes: item.notes,
                company_id: companyId
              });

            // Create transfer item for detailed tracking
            await supabase
              .from('transfer_items')
              .insert({
                movement_id: movement.id,
                product_type: 'equipment',
                product_id: item.productId,
                quantity: 1,
                notes: item.notes,
                company_id: companyId,
                created_by: user?.id
              });
          }
        }
      }

      return { success: true, movement };
    } catch (error) {
      console.error('Erro ao processar transferência multi-item:', error);
      throw error;
    }
  };

  // Third-party cylinders functions
  const getThirdPartyCylinders = async () => {
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('cylinders')
      .select(`
        id,
        serial_number,
        gas_status,
        status,
        entry_reason,
        entry_date,
        expected_return_date,
        returned_at,
        notes,
        products!product_id(name),
        customers!inner(name),
        entry_responsible:employees!entry_responsible_id(name)
      `)
      .eq('company_id', company?.id)
      .eq('owner_type', 'third_party')
      .order('entry_date', { ascending: false });

    if (error) throw error;

    return data.map(cylinder => ({
      id: cylinder.id,
      serial_number: cylinder.serial_number,
      cylinder_type_name: (cylinder as any).products?.name || 'N/A',
      gas_status: cylinder.gas_status,
      owner_customer_name: cylinder.customers.name,
      entry_reason: cylinder.entry_reason,
      entry_date: cylinder.entry_date,
      expected_return_date: cylinder.expected_return_date,
      returned_at: cylinder.returned_at,
      entry_responsible_name: cylinder.entry_responsible?.name || '',
      status: cylinder.status,
      notes: cylinder.notes || ''
    }));
  };

  const createThirdPartyCylinder = async (cylinderData: {
    serial_number: string;
    cylinder_type_id?: string; // Legacy support
    product_id?: string; // New unified products table
    owner_customer_id: string;
    gas_status: string;
    entry_reason: string;
    entry_responsible_id: string;
    expected_return_date?: string;
    notes?: string;
  }) => {
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('cylinders')
      .insert({
        ...cylinderData,
        company_id: company?.id,
        created_by: user.id,
        owner_type: 'third_party',
        entry_date: new Date().toISOString(),
        status: 'available',
        gas_status: cylinderData.gas_status as 'full' | 'empty' | 'unknown'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  };

  const returnThirdPartyCylinder = async (cylinderId: string, returnData: {
    return_responsible_id: string;
    gas_status: string;
    return_notes?: string;
  }) => {
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('cylinders')
      .update({
        returned_at: new Date().toISOString(),
        return_responsible_id: returnData.return_responsible_id,
        gas_status: returnData.gas_status as 'full' | 'empty' | 'unknown',
        notes: returnData.return_notes ? 
          `${returnData.return_notes}` : undefined,
        status: 'available'
      })
      .eq('id', cylinderId)
      .eq('company_id', company?.id)
      .eq('owner_type', 'third_party')
      .select()
      .single();

    if (error) throw error;
    return data;
  };

  return {
    companyId: effectiveCompanyId,
    activeCompany,
    ensureCompanyContext,
    isContextReady,
    loading,
    // Customers
    getCustomers,
    insertCustomer,
    // Employees
    getEmployees,
    insertEmployee,
    getAvailableUsers,
    linkEmployeeToUser,
    // Cylinders
    getCylinders,
    insertCylinder,
    // Equipment
    getEquipment,
    insertEquipment,
    // Job positions
    getJobPositions,
    insertJobPosition,
    // Deposits
    getDeposits,
    insertDeposit,
    getDepositCylinders,
    getDepositEquipment,
    // Equipment categories
    getEquipmentCategories,
    insertEquipmentCategory,
    // Brands
    getBrands,
    insertBrand,
    // Measurement units
    getMeasurementUnits,
    insertMeasurementUnit,
    updateMeasurementUnit,
    deleteMeasurementUnit,
    // Stock entries
    getStockEntries,
    insertStockEntry,
    // Loans
    getLoans,
    insertLoan,
    generateLoanNumber,
    getActiveLoans,
    processLoanReturn,
    getLoanReturns,
    getLoanReturnEvents,
    // Rentals
    getRentals,
    generateRentalNumber,
    // Movements
    getMovements,
    insertMovement,
    insertMovementItem,
    // Cylinder types
    getCylinderTypes,
    // Equipment types
    getEquipmentTypes,
    // Transfer
    processTransfer,
    processMultiItemTransfer,
    // Generic helpers
    updateRecord,
    deleteRecord,
    // Third-party cylinders operations
    getThirdPartyCylinders,
    createThirdPartyCylinder,
    returnThirdPartyCylinder,
    // Product stats
    getProductStats
  };
};